import { computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Model } from '../../model/model.model';
import { ModelEventBus } from '@web-portal/domains/shared/event-bus';

/**
 * @internal
 * @description
 * Model store.
 */
export class ModelStore {
  private readonly _models = signal<Model[]>([]);
  readonly models = this._models.asReadonly();

  /** Maximum 4 recent models sorted by changed/created date (newest first) */
  readonly recentModels = computed(() => {
    const allModels = this._models();
    return [...allModels]
      .sort((a, b) => {
        const dateA = new Date(a.changed || a.created || 0).getTime();
        const dateB = new Date(b.changed || b.created || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 4);
  });

  private readonly modelEventBus = inject(ModelEventBus);

  constructor() {
    this.modelEventBus
      .on$<Model | Model[]>('[Model] updated')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        this.setModels(event);
      });

    this.modelEventBus
      .on$<Model>('[Model] created')
      .pipe(takeUntilDestroyed())
      .subscribe(model => {
        this._models.update(models => [...models, model]);
      });

    this.modelEventBus
      .on$<string>('[Model] deleted')
      .pipe(takeUntilDestroyed())
      .subscribe(modelId => {
        this._models.update(models => models.filter(m => m.id !== modelId));
      });
  }

  private setModels(models: Model | Model[]): void {
    if (!Array.isArray(models)) {
      this._models.update(oldState => [...oldState, models]);
      return;
    }

    this._models.set(models);
  }
}
